import React, { useCallback, useEffect, useState } from 'react';
import { Package, RefreshCw, DollarSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  past_due: 'bg-amber-50 text-amber-700 border-amber-100',
  cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
};

const Stat = ({ label, value, testid }) => (
  <div className="bg-white rounded-lg border border-gray-100 p-4" data-testid={testid}>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    <p className="text-[10px] uppercase tracking-wider text-gray-400">{label}</p>
  </div>
);

export default function GearBoxTab() {
  const token = sessionStorage.getItem('admin_token');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/gearbox`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('load failed');
      setData(await res.json());
    } catch (e) { setErr(e.message); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (err) return <p className="text-sm text-red-500">{err}</p>;
  if (!data) return <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />;

  const s = data.summary || {};
  return (
    <div className="space-y-6" data-testid="gearbox-admin-tab">
      <div>
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Package className="w-5 h-5 text-emerald-600" /> Gear Box Subscriptions</h2>
        <p className="text-sm text-gray-500">Statuses refresh live from Stripe on load. Fulfillment is manual — ship each active subscriber's box in the first week of the month.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Active subscribers" value={s.active ?? 0} testid="gearbox-stat-active" />
        <Stat label="MRR" value={`$${((s.mrr_cents || 0) / 100).toFixed(0)}`} testid="gearbox-stat-mrr" />
        <Stat label="Past due" value={s.past_due ?? 0} testid="gearbox-stat-pastdue" />
        <Stat label="Cancelled" value={s.cancelled ?? 0} testid="gearbox-stat-cancelled" />
      </div>

      {Object.keys(s.by_tier || {}).length > 0 && (
        <div className="flex gap-2">
          {Object.entries(s.by_tier).map(([t, n]) => (
            <span key={t} className="px-2.5 py-1.5 bg-gray-50 border border-gray-100 rounded text-xs text-gray-600 capitalize">{t}: <strong>{n}</strong></span>
          ))}
        </div>
      )}

      {(data.subscriptions || []).length === 0 ? (
        <p className="text-xs text-gray-400 bg-gray-50 rounded-lg p-5" data-testid="gearbox-empty">No subscribers yet. The Gear Box page is live at /gear-box — the /shop banner and footer link both point to it.</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm" data-testid="gearbox-subs-table">
            <thead>
              <tr className="border-b border-gray-100 text-left text-[11px] uppercase tracking-wider text-gray-400">
                <th className="px-4 py-3">Email</th><th className="px-4 py-3">Tier</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3">Ships to</th>
                <th className="px-4 py-3">Started</th><th className="px-4 py-3">Renews</th>
              </tr>
            </thead>
            <tbody>
              {data.subscriptions.map((sub) => (
                <tr key={sub.subscription_id} className="border-b border-gray-50">
                  <td className="px-4 py-2.5 text-gray-700 text-xs">{sub.email}</td>
                  <td className="px-4 py-2.5 font-semibold text-gray-900 capitalize">{sub.tier} <span className="text-gray-400 font-normal">${(sub.amount / 100).toFixed(0)}/mo</span></td>
                  <td className="px-4 py-2.5 text-xs"><span className={`px-2 py-0.5 rounded border capitalize ${STATUS_STYLE[sub.status] || 'bg-gray-50 text-gray-600 border-gray-100'}`}>{sub.status}</span></td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs">{sub.shipping?.name ? `${sub.shipping.name} · ${sub.shipping.city || ''} ${sub.shipping.state || ''} ${sub.shipping.postal_code || ''}` : '—'}</td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs">{(sub.created_at || '').slice(0, 10)}</td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs">{(sub.current_period_end || '').slice(0, 10) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
